import { GA4_REFUSALS } from '@/api/ga4'
import { ApiError } from '@/lib/api'

/**
 * The part of GA4 error handling screens share, in the pieces each screen
 * needs: Google's refusals of a connect, a busy or failing provider, and a
 * deployment with no key. What a 403, 404, 409 or 429 means depends on the
 * screen (connecting, managing a property, reading traffic), so each screen owns
 * that map in its own folder and carries only the kinds it can emit. This
 * module imports none of them.
 */

/** Every kind more than one screen can emit, each with its one wording. */
export const SHARED_WORDING = {
  grant_missing: (email: string | null) =>
    `Google Analytics refused access. In GA4 open Admin › Property access management and add ${email ?? 'the service account shown above'} as a Viewer on this property, then try again.`,
  property_not_found:
    'Google Analytics has no property with that ID. Copy the numeric property ID from GA4 Admin › Property settings (not the "G-…" measurement ID) and try again.',
  ownership_not_proven:
    "None of that property's web data streams is one of your websites, so it cannot be connected to this account. Check the property ID, or contact TmT support if the website is missing.",
  provider_down: 'Google Analytics did not answer just now. Wait a few minutes and try again.',
  rate_limited: 'Too many requests just now. Wait a minute and try again.',
  not_configured:
    "Google Analytics is not set up on this environment yet, or TmT's Google project needs attention. Nothing about your property is wrong — contact TmT support.",
} as const

export type SharedKind = keyof typeof SHARED_WORDING
export type RefusalKind = Extract<SharedKind, 'grant_missing' | 'property_not_found' | 'ownership_not_proven'>
export type ProviderDownKind = Extract<SharedKind, 'provider_down'>
export type RateLimitedKind = Extract<SharedKind, 'rate_limited'>
export type NotConfiguredKind = Extract<SharedKind, 'not_configured'>

/** A sentence, or one that names the service account. */
export type Wording = string | ((email: string | null) => string)

/** The shared wording for just the kinds a screen can emit, so a screen carries no copy it cannot show. */
export function pickWording<K extends SharedKind>(...kinds: readonly K[]): Record<K, Wording> {
  const picked = {} as Record<K, Wording>
  for (const kind of kinds) picked[kind] = SHARED_WORDING[kind]
  return picked
}

/** Google's refusals of a connect (a 422 whose message says so). */
export function classifyRefusal(error: ApiError): RefusalKind | null {
  if (error.status !== 422) return null
  const { grantMissing, propertyNotFound, ownershipNotProven } = GA4_REFUSALS
  if (grantMissing.test(error.message)) return 'grant_missing'
  if (propertyNotFound.test(error.message)) return 'property_not_found'
  if (ownershipNotProven.test(error.message)) return 'ownership_not_proven'
  return null
}

/** Google did not answer (a 502). */
export function classifyProviderDown(error: ApiError): ProviderDownKind | null {
  return error.status === 502 ? 'provider_down' : null
}

/** A request limit that is not the import's: "wait and retry". The manage screen words its own 429 as the import cooldown. */
export function classifyRateLimited(error: ApiError): RateLimitedKind | null {
  return error.status === 429 ? 'rate_limited' : null
}

/**
 * A 503 is "not configured" only when the server says so; any other 503 is a
 * service that will answer shortly, and falls through to the screen's retry
 * wording. Only the screens that call Google with the key (connect, manage) ask.
 */
export function classifyNotConfigured(error: ApiError): NotConfiguredKind | null {
  return error.status === 503 && GA4_REFUSALS.notConfigured.test(error.message) ? 'not_configured' : null
}

/**
 * A screen's whole error handling in one declaration: which kind a status
 * means to it (`status`, a kind or a function of the error), the shared
 * classifiers to try for the rest (`rules`, in order) and the wording for
 * exactly the kinds it can emit. Returns the sentence for a failure.
 */
export function screenErrors<K extends string>(config: {
  status?: Partial<Record<number, K | ((error: ApiError) => K | null)>>
  rules?: ReadonlyArray<(error: ApiError) => K | null>
  wording: Record<K, Wording>
}): (error: unknown, email: string | null, fallback: string) => string {
  const { status = {}, rules = [], wording } = config
  const classify = (error: ApiError): K | null => {
    const mapped = status[error.status]
    if (mapped !== undefined) return typeof mapped === 'function' ? mapped(error) : mapped
    for (const rule of rules) {
      const kind = rule(error)
      if (kind) return kind
    }
    return null
  }
  return (error, email, fallback) => describeGa4Error(error, { classify, wording, email, fallback })
}

/**
 * The sentence for a failure: the screen's wording for the kind its classifier
 * found, else the server's own message; a server error's text is for the log,
 * not the person, so a 5xx and a non-API error get the fallback.
 */
export function describeGa4Error<K extends string>(
  error: unknown,
  options: { classify: (error: ApiError) => K | null; wording: Record<K, Wording>; email?: string | null; fallback: string },
): string {
  if (!(error instanceof ApiError)) return options.fallback
  const kind = options.classify(error)
  if (kind) {
    const wording = options.wording[kind]
    return typeof wording === 'function' ? wording(options.email ?? null) : wording
  }
  return error.status >= 500 ? options.fallback : error.message
}
